import { always, identity, ifElse, invertObj, pick, pipe, replace, slice, toLower, toUpper, when, eqBy } from 'ramda';

export class LanguageService {
    NGMLocalesMapping = {
        ZH: 'zh-cn',
        ZH_CN: 'zh-cn'
    };
    imsLocalesMappingRaw = {
        /* eslint-disable camelcase */
        at_AT: 'de-at',
        ca_CA: 'en-ca',
        en_AU: 'en-au',
        en_CA: 'en-ca',
        en_GB: 'en-gb',
        es_MX: 'es-mx',
        fr_QC: 'fr-ca',
        jp_JP: 'ja',
        pt_BR: 'pt-br',
        za_ZA: 'en-za',
        zh_CN: 'zh-cn',
        zh_TW: 'zh-tw'
        /* eslint-enable camelcase */
    };

    getLanguage = (lang) => {
        if (!lang) {
            return null;
        }

        const parsedLang = pipe(
            when(
                x => x.length === 2,
                toLower
            ),
            replace('-', '_')
        )(lang);

        return this.allowedLocales.find(eqBy(toLower, parsedLang))
            || this.getLanguageForAlias(parsedLang)
            || this.shortLocalesMappingInverted[parsedLang]
            || this.imsLocalesMappingInverted[parsedLang]
            || null;
    };

    getAliasForLanguage = (lang) => this.aliasLocaleMapping[lang] || lang;

    getLanguageForAlias = (alias) => this.aliasLocaleMappingInverted[alias];

    getShortLanguage = (lang) => this.shortLocalesMapping[lang];

    getNGMLanguage = (lang) => this.NGMLocalesMapping[toUpper(lang)] || this.getTwoLetterLanguage(lang);

    convertFromIMS = (lang) => this.imsLocalesMappingInverted[lang] || this.defaultLocale;

    getImsLocalesMapping = (allowedLocales) => pick(allowedLocales, this.imsLocalesMappingRaw);

    /**
     * Retrieve current 2-symbols language
     * @param [lang] {String}
     * @returns {String}
     */
    getTwoLetterLanguage = (lang) => pipe(
        this.getLanguage,
        ifElse(
            identity,
            slice(0, 2),
            always(null)
        )
    )(lang);

    convertToIMS = (lang) => {
        let validLang = lang;

        if (!this.allowedLocales.includes(validLang)) {
            validLang = this.shortLocalesMappingInverted[validLang];
        }

        return this.imsLocalesMapping[validLang] || this.getTwoLetterLanguage(validLang);
    };

    getShortLocaleMapping = (locales = []) =>
        locales.reduce((shortLocales, locale) => {
            const [shortLocale] = locale.split(/[_-]/);

            shortLocales[locale] = invertObj(shortLocales)[shortLocale] ? locale : shortLocale;

            return shortLocales;
        }, {});

    /**
     * Get pathLang:
     * aligned with "pathLang" router parameter;
     * combination of 'lang' hooks.
     * @function
     * @param lang {String} language in 5-symbols format
     * @returns {String}
     * @example
     * // ""
     * getPathLang('en_GB') if "en_GB" is default language
     * // "brazil-pt"
     * getPathLang('pt_BR') if "pt_BR" has configured alias "brazil-pt"
     * // "pt"
     * getPathLang('pt_BR') if allowedLocales does not have language with starting "pt" before "pt_BR": ['en_GB', 'pt_BR', 'de_DE']
     * // "pt_BR"
     * getPathLang('pt_BR') if allowedLocales has language with starting "pt" before "pt_BR": ['en_GB', 'pt_PT', 'pt_BR', 'de_DE'];
     * in this case "pt" pathLang is reserved for "pt_PT" language
     */
    getPathLang = (lang) => {
        if (lang === this.defaultLocale && this.getLanguage(lang) === this.defaultLocale) {
            return '';
        }

        const pathLangAlias = this.getAliasForLanguage(lang);

        return pathLangAlias === lang ? this.getShortLanguage(lang) : pathLangAlias;
    };

    // todo this should not be taken directly from Variables.

    get defaultLocale() {
        return window.Playtech?.Variables?.defaultLocale || '';
    }

    get allowedLocales() {
        return window.Playtech?.Variables?.allowedLocales || [];
    }

    get allowedLocalesLowerCased() {
        return this.allowedLocales.map(toLower);
    }

    get aliasLocaleMapping() {
        const aliasLocaleMapping = window.Playtech?.Variables?.aliasLocaleMapping || {};

        return pick(this.allowedLocales, aliasLocaleMapping);
    }

    get aliasLocaleMappingInverted() {
        return invertObj(this.aliasLocaleMapping);
    }

    get imsLocalesMapping() {
        return this.getImsLocalesMapping(this.allowedLocales);
    }

    get imsLocalesMappingInverted() {
        return invertObj(this.imsLocalesMapping);
    }

    get shortLocalesMapping() {
        return this.getShortLocaleMapping(this.allowedLocales);
    }

    get shortLocalesMappingInverted() {
        return invertObj(this.shortLocalesMapping);
    }
}

const languageService = new LanguageService();

export { languageService };
